import React from 'react';
import { theme, st } from '../constants.js';
import { FI, FS, SingleChip } from '../components/FormPrimitives.js';
import { SectionHeader, Sub } from '../components/SectionLayout.js';

const FunctionalCapacity = ({ data, upd }) => (
  <div style={{ animation: 'fadeIn 0.3s' }}>
    <div style={st.card}>
      <SectionHeader icon="🏃" title="Functional Capacity & Activity Tolerance" subtitle="What can they actually DO, and what stops them?" color="#f59e0b" />

      <Sub title="🚶 Current Mobility Level">
        <div style={st.grid3}>
          <FS label="Current Mobility Status" value={data.currentMobilityLevel} onChange={e => upd('currentMobilityLevel', e.target.value)}
            options={['Bedbound', 'Sitting out of bed only', 'Transfers only (stand + pivot)', 'Walking short distances (<10m)', 'Walking with aid (10–50m)', 'Walking without aid (>50m)', 'Independent community ambulator']} />
          <FI label="Maximum Walking Distance" value={data.walkingDistance} onChange={e => upd('walkingDistance', e.target.value)} placeholder="e.g. 30m before stopping" />
          <FS label="Walking Aid" value={data.walkingAid} onChange={e => upd('walkingAid', e.target.value)}
            options={['None', 'Stick / cane', 'Elbow crutches', 'Frame (standard)', 'Frame (wheeled)', 'Wheelchair', 'Hoist']} />
        </div>
      </Sub>

      <Sub title="📏 Dyspnoea Scales">
        <div style={st.grid3}>
          <FS label="mMRC Dyspnoea Scale" value={data.mmrc} onChange={e => upd('mmrc', e.target.value)}
            options={[{ v: '0', l: '0 — Only with strenuous exercise' }, { v: '1', l: '1 — Hurrying on level / slight incline' }, { v: '2', l: '2 — Slower than peers on level / must stop on level' }, { v: '3', l: '3 — Stops after 100m or few minutes' }, { v: '4', l: '4 — Too breathless to leave house / dressing' }]} />
          <FS label="Borg Dyspnoea (Baseline)" value={data.borgBaseline} onChange={e => upd('borgBaseline', e.target.value)}
            options={['0 — Nothing', '0.5 — Very very slight', '1 — Very slight', '2 — Slight', '3 — Moderate', '4 — Somewhat severe', '5 — Severe', '6', '7 — Very severe', '8', '9 — Very very severe', '10 — Maximal']} />
          <FS label="Borg Dyspnoea (Peak / Exercise)" value={data.borgPeak} onChange={e => upd('borgPeak', e.target.value)}
            options={['0 — Nothing', '1 — Very slight', '2 — Slight', '3 — Moderate', '4 — Somewhat severe', '5 — Severe', '6', '7 — Very severe', '8', '9 — Very very severe', '10 — Maximal']} />
        </div>
      </Sub>

      <Sub title="🚦 Limiting Symptom — THIS IS THE KEY CLINICAL DECISION" reasoning={<>
        Stops due to dyspnoea at 50m → breathing is the LIMITER → focus on breathing strategies, positioning, pursed-lip breathing.<br />
        Stops due to leg fatigue → deconditioning is LIMITER → progressive strengthening and endurance training.<br />
        Stops due to SpO₂ drop → gas exchange is LIMITER → may need long-term O₂, optimise positioning, consider prone.<br />
        Stops due to chest pain → cardiac LIMITER → medical clearance needed before progressing PT.
      </>}>
        <div style={st.grid2}>
          <SingleChip label="What STOPS the patient?" value={data.limitingSymptom} onChange={v => upd('limitingSymptom', v)}
            options={['Dyspnoea (breathing)', 'O₂ desaturation', 'Leg fatigue / weakness', 'Chest pain / cardiac', 'General fatigue', 'Pain (musculoskeletal)', 'Dizziness / lightheaded', 'Patient request']} />
          <SingleChip label="Primary Limiting Domain" value={data.limitingDomain} onChange={v => upd('limitingDomain', v)}
            options={['Ventilation', 'Gas Exchange', 'Cardiac', 'Secretions', 'Deconditioning / Musculoskeletal', 'Psychological', 'Multi-factorial']} />
        </div>
      </Sub>

      <Sub title="📊 6-Minute Walk Test (6MWT)">
        <div style={st.grid4}>
          <FI label="Distance Walked (m)" value={data.sixMWD} onChange={e => upd('sixMWD', e.target.value)} />
          <FI label="Predicted (m)" value={data.sixMWDpredicted} onChange={e => upd('sixMWDpredicted', e.target.value)} placeholder="Age/sex/height formula" />
          <FI label="% Predicted" value={data.sixMWDpercent} onChange={e => upd('sixMWDpercent', e.target.value)} placeholder="<70% = impaired" />
          <FI label="Stop Reason (if not completed)" value={data.stopReason} onChange={e => upd('stopReason', e.target.value)} />
        </div>
      </Sub>

      <Sub title="📈 Dynamic Exercise Assessment (Pre → Peak → Recovery)">
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Parameter', 'Pre-Exercise', 'Peak Exertion', '1 Min Recovery', '3 Min Recovery', '5 Min Recovery'].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontWeight: '700', color: theme.textMuted, fontSize: '0.73rem', borderBottom: `2px solid ${theme.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { lbl: 'HR (bpm)',   flds: ['preHR',  'peakHR',  'rec1HR',  'rec3HR',  'rec5HR']  },
                { lbl: 'BP (mmHg)',  flds: ['preBP',  'peakBP',  '',        '',        '']        },
                { lbl: 'RR (/min)',  flds: ['preRR',  'peakRR',  '',        '',        '']        },
                { lbl: 'SpO₂ (%)',  flds: ['preSPO2','peakSPO2','',        'rec3SPO2','rec5SPO2'] },
              ].map(row => (
                <tr key={row.lbl} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '7px 10px', fontWeight: '700', color: theme.textMain, fontSize: '0.8rem' }}>{row.lbl}</td>
                  {row.flds.map((f, i) => (
                    <td key={i} style={{ padding: '5px 6px' }}>
                      {f ? <input className="cp-table-input" style={{ ...st.input, padding: '7px 9px', fontSize: '0.82rem' }} value={data[f] || ''} onChange={e => upd(f, e.target.value)} placeholder="—" /> : <span style={{ color: '#e2e8f0', padding: '0 8px' }}>—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="cp-scroll-hint" style={{ display: 'none', textAlign: 'center', fontSize: '0.72rem', color: theme.textMuted, marginTop: '5px', letterSpacing: '0.02em' }}>
          ← swipe to see full table →
        </div>

        {data.hrRecovery1min && (
          <div style={{ ...(data.abnormalHRRecovery ? st.hintDanger : st.hint), marginTop: '12px' }}>
            {data.abnormalHRRecovery
              ? `⚠️ HR Recovery at 1 min = ${data.hrRecovery1min} bpm — ABNORMAL (<12 bpm). Increased cardiac risk. Do not progress load without medical review.`
              : `✅ HR Recovery at 1 min = ${data.hrRecovery1min} bpm — Normal (≥12 bpm). Good autonomic recovery.`}
          </div>
        )}
      </Sub>

      <Sub title="🏠 ADL & Discharge Readiness">
        <div style={st.grid3}>
          <FS label="ADL Independence" value={data.adlIndependence} onChange={e => upd('adlIndependence', e.target.value)}
            options={['Independent in all ADLs', 'Needs prompting only', 'Assistance with showering / dressing', 'Assistance with most ADLs', 'Fully dependent']} />
          <FS label="Stairs Capacity" value={data.stairsCapacity} onChange={e => upd('stairsCapacity', e.target.value)}
            options={['Full flight independently', 'Full flight with rail', 'Half flight only', 'A few steps only', 'Unable', 'Not tested']} />
          <FS label="Discharge Readiness" value={data.dischargeReadiness} onChange={e => upd('dischargeReadiness', e.target.value)}
            options={['Ready for discharge', 'Requires further inpatient PT', 'Requires transfer to rehab', 'Ongoing community PT needed', 'Not yet assessable']} />
        </div>
        <FI label="Functional Notes" type="textarea" value={data.functionalNotes} onChange={e => upd('functionalNotes', e.target.value)} placeholder="Barriers to discharge, progress made, pending goals..." />
      </Sub>
    </div>
  </div>
);

export default FunctionalCapacity;
